import React, { useState, useEffect } from "react";
import {
  Typography,
  Grid,
  Container,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import api from "../globals/axios";
import { useParams } from "react-router-dom";
import LocateLand from "../components/LocateLand";
import CustomLabelValueTable from "../components/Custom/CustomLabelValueTable";
import { LoadScript } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY } from "../globals/const";

export default function CropTracePublic() {
  const { cropId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [cropData, setCropData] = useState({});
  const [fertilizers, setFertilizers] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [coordinates, setCoordinates] = useState({});
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      const _cropId = cropId.split("-").pop();
      try {
        const res = await api.get(`/farmer/crop-detailed-view/${_cropId}`);
        if (res.status !== 200) {
          alert("Could not load the crop data");
          return;
        }
        const data = res.data;
        setCropData(data.crop_data);
        setFertilizers(data.pesticide_fertilizers);
        setLabTests(data.lab_tests);
        setCoordinates({
          latitude: data.crop_data?.farmland?.polygon[0]?.lat,
          longitude: data.crop_data?.farmland?.polygon[0]?.lng,
        });
      } catch (error) {
        console.error("Error loading farm data:", error);
        alert("Could not load the crop data");
        return;
      }
      setIsLoading(false);
    };

    loadProduct();
  }, []);

  if (isLoading || !cropData?.farmland) {
    return (
      <div>
        <Typography
          variant="h3"
          textAlign="center"
          style={{ marginTop: "10px", marginBottom: "10px" }}
        >
          Loading...
        </Typography>
      </div>
    );
  }

  return (
    <Container maxWidth="sm">
      <Grid
        container
        // spacing={1}
        justify="center"
        direction="column"
        style={{ marginTop: "10px" }}
      >
        <Grid container item>
          <Grid item xs>
            <Box
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-around",
                marginTop: "20px",
              }}
            >
              <Typography variant="h5" mb={2} ml={1}>
                Crop Information
              </Typography>

              <Paper>
                <Box m={2}>
                  <CustomLabelValueTable
                    data={{
                      id: cropId,
                      farmer: cropData.farmer?.full_name,
                      farm_name: cropData.farmland?.farm_name,
                      sowing_date: cropData.sowing_date,
                      seed_type: cropData.seed_type,
                      commodity: cropData.commodity?.name,
                      estimated_harvest: cropData.estimated_harvest,
                      estimated_harvest_date: cropData.estimated_harvest_date,
                    }}
                    fields={[
                      { label: "Crop ID", key: "id" },
                      { label: "Farmer Name", key: "farmer" },
                      { label: "Farm Name", key: "farm_name" },
                      { label: "Sowing Date", key: "sowing_date" },
                      { label: "Seed Type", key: "seed_type" },
                      { label: "Commodity", key: "commodity" },
                      {
                        label: "Estimated Harvest",
                        key: "estimated_harvest",
                      },
                      {
                        label: "Harvest Date",
                        key: "estimated_harvest_date",
                      },
                    ]}
                  />
                  <LoadScript
                    googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                    onLoad={() => setIsScriptLoaded(true)}
                  >
                    <LocateLand
                      center={{
                        lat: cropData?.farmland?.polygon[0]?.lat,
                        lng: cropData?.farmland?.polygon[0]?.lng,
                      }}
                      setCenter={() => {}}
                      path={cropData?.farmland?.polygon}
                      setPath={() => {}}
                      editable={false}
                      setCurrentLocation={() => {}}
                      isScriptLoaded={isScriptLoaded}
                    />
                  </LoadScript>
                </Box>
              </Paper>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Crop Photos</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {!cropData.sowing_file && !cropData.farmland?.farm_doc ? (
                    <Typography>No crop photos added</Typography>
                  ) : (
                    <Box m={2}>
                      <img src={cropData.sowing_file} width={"100%"} />
                      <img src={cropData.farmland?.farm_doc} width={"100%"} />
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Lab Tests</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    {labTests.length === 0 ? (
                      <Typography>No lab test added</Typography>
                    ) : (
                      labTests.map((labTest, index) => (
                        <Paper key={index}>
                          <Box p={2}>
                            <CustomLabelValueTable
                              data={labTest}
                              fields={[
                                { label: "Lab Name", key: "lab_name" },
                                { label: "Test Date", key: "test_date" },
                                { label: "Report Date", key: "report_date" },
                                { label: "Remarks", key: "remarks" },
                              ]}
                            />
                            <Typography>
                              <a
                                href={labTest.lab_report}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View Report
                              </a>
                            </Typography>
                          </Box>
                        </Paper>
                      ))
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Fertilizers/Pesticides</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    {fertilizers.length === 0 ? (
                      <Typography>No fertilizers added</Typography>
                    ) : (
                      fertilizers.map((fertilizer, index) => (
                        <Paper key={index}>
                          <Box p={2}>
                            <CustomLabelValueTable
                              data={fertilizer}
                              fields={[
                                { label: "Type", key: "application_type" },
                                {
                                  label: "Brand Name",
                                  key: "product_brand_name",
                                },
                                { label: "Method", key: "method" },
                                { label: "Quantity", key: "quantity" },
                                {
                                  label: "Application Date",
                                  key: "application_date",
                                },
                              ]}
                            />
                            <img
                              src={fertilizer.application_file}
                              width={"100%"}
                            />
                          </Box>
                        </Paper>
                      ))
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Satellite Data</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box m={2}>
                    <CustomLabelValueTable
                      data={{
                        latitude: coordinates.latitude,
                        longitude: coordinates.longitude,
                        soil_moisture: 28.81,
                        pest_probability: 0.19,
                        crop_growth: 50,
                      }}
                      fields={[
                        { label: "Latitude", key: "latitude" },
                        { label: "Longitude", key: "longitude" },
                        { label: "Soil Moisture", key: "soil_moisture" },
                        {
                          label: "Crop Growth (MT/ha)",
                          key: "crop_growth",
                        },
                      ]}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Decarbonization Efforts</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>No decarbonisation process added</Typography>
                </AccordionDetails>
              </Accordion>

              <Typography variant="h6" mt={2} ml={1}>
                QR Code
              </Typography>
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <img
                  src={`https://qrcode.tec-it.com/API/QRCode?data=${window.location.href}`}
                  alt="QR code"
                  width="200"
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
}
